import { useState, useEffect, useRef, useCallback } from 'react';
import { usePostApi } from './post_api_hook/usePostApi';
import { url } from '../utils/confserveur';

/** Le serveur périme un jeton non réclamé en 2 min : en redemander un toutes
 *  les 90 s garde toujours un QR valide à l'écran, et l'ancien reste bon
 *  jusqu'à sa fin (pas de couperet pour le prof qui scanne à la seconde du
 *  changement). */
export const RENOUVELLEMENT_MS = 90 * 1000;

/**
 * Le cycle de vie du QR projeté (chantier jeton, 2026-09-04), vu du PC.
 *
 * Tant que la séance attend (`actif`) et qu'aucun appareil n'est relié, le
 * hook demande un jeton, puis un autre toutes les 90 s. Il s'arrête de
 * lui-même dès que la présence, poussée par le flux, dit « appareil relié »,
 * ou dès que le serveur refuse (409 : un appareil est DÉJÀ relié, cas d'une
 * projection rouverte). `detacher` révoque l'appareil relié ; la présence
 * retombe par le flux et le cycle repart.
 *
 * Coût : une requête toutes les 90 s par projection ouverte, pendant les
 * quelques minutes d'attente seulement. Rien pendant le quizz.
 */
export const useJetonScan = ({ sessionId, actif, appareilRelie }) => {
    const [, doPost] = usePostApi(null, null);
    // `doPost` est recréé à chaque render : le garder dans une ref évite que
    // le minuteur ne redémarre à chaque passage du composant.
    const doPostRef = useRef(doPost);
    doPostRef.current = doPost;

    const [jeton, setJeton] = useState(null);
    // Dernier refus de `jeton-scan`, lisible, pour que l'écran ne reste pas
    // sur « Préparation du QR… » sans dire pourquoi.
    const [erreurJeton, setErreurJeton] = useState(null);
    // Le serveur a répondu « appareil déjà relié » : même effet qu'une
    // présence reçue par le flux, en attendant qu'elle arrive.
    const [refuse, setRefuse] = useState(false);
    const relie = Boolean(appareilRelie) || refuse;
    // Incrémenté par `relier()` : relance le cycle de demande même quand rien
    // d'autre n'a bougé (la présence que la projection croit peut être fausse).
    const [generation, setGeneration] = useState(0);

    useEffect(() => {
        if (!sessionId || !actif || relie) {
            setJeton(null);
            return undefined;
        }
        let vivant = true;
        const demander = async () => {
            const r = await doPostRef.current({}, `${url}sessions-qcm/${sessionId}/jeton-scan/`);
            if (!vivant) return;
            if (r?.success && r.data?.jeton) {
                setJeton(r.data.jeton);
                setErreurJeton(null);
                return;
            }
            const statut = r?.error?.response?.status;
            if (statut === 409) {
                setRefuse(true);
                setJeton(null);
                setErreurJeton(null);
                return;
            }
            // Autre échec : on garde le QR précédent s'il y en a un, le minuteur
            // retentera, et la raison est dite.
            const detail = r?.error?.response?.data?.error || r?.error?.response?.data?.detail;
            setErreurJeton(statut ? `refus ${statut}${detail ? ` : ${detail}` : ''}` : 'pas de réponse du serveur');
        };
        demander();
        const minuteur = setInterval(demander, RENOUVELLEMENT_MS);
        return () => {
            vivant = false;
            clearInterval(minuteur);
        };
    }, [sessionId, actif, relie, generation]);

    // Renvoie null si le détachement est accepté, sinon la raison lisible du
    // refus (statut + message du serveur), pour que la projection l'affiche.
    // La présence reste la SEULE source de « relié » : c'est à l'appelant de
    // la mettre à jour sur un succès (même état que le flux, que le prochain
    // événement écrase). Un drapeau local à part a été essayé le 2026-09-12
    // et retiré : il ne se remettait pas quand la présence n'avait jamais
    // bougé, et le QR restait affiché après qu'un appareil s'était relié.
    const detacher = useCallback(async () => {
        const r = await doPostRef.current({}, `${url}sessions-qcm/${sessionId}/detacher-scan/`, { timeoutMs: 8000 });
        if (r?.success) {
            // Le jeton d'avant est mort avec l'appareil détaché : on l'oublie
            // TOUT DE SUITE (jamais redessiné, jamais réclamé), le cycle en
            // redemande un neuf dès que la présence retombe.
            setJeton(null);
            setRefuse(false);
            return null;
        }
        const statut = r?.error?.response?.status;
        const detail = r?.error?.response?.data?.error || r?.error?.response?.data?.detail;
        return statut ? `refus ${statut}${detail ? ` : ${detail}` : ''}` : 'pas de réponse du serveur';
    }, [sessionId]);

    /**
     * « Relier un téléphone » (projection, en plein quizz) : ne se fie PAS à ce
     * que la projection croit (une présence manquée lui fait croire « non
     * relié » alors qu'un téléphone l'est, et elle réaffichait l'ancien QR,
     * déjà consommé : « ce lien a déjà servi »). Donc, toujours : détacher ce
     * qu'il y a à détacher, oublier le jeton, en redemander un neuf. Renvoie
     * la raison d'un refus de détachement, ou null.
     */
    const relier = useCallback(async () => {
        setJeton(null);
        const refus = await detacher();
        setRefuse(false);
        setGeneration((g) => g + 1);
        return refus;
    }, [detacher]);

    return { jeton, relie, detacher, relier, erreurJeton, refuse };
};

export default useJetonScan;
